import {
  DailyAudio,
  DailyProvider,
  DailyVideo,
  useParticipantIds
} from '@daily-co/daily-react';
import { conversationStore } from 'entities/tavus/conversation';
import {
  DAILY_CALL_STATES,
  events,
  transcriptionEvents
} from 'features/daily-call/model';
import { dailyCallStore } from 'features/daily-call/model/dailyCallStore';
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { MAIN_ROUTE } from 'shared/model';
import { Loader, ToolTip } from 'shared/ui';
import { TavusControlBar } from 'widgets/call-controls';
import { tavusPersonaStore } from 'entities/tavus/persona';
import { cn, useFullScreen, useMouseVisibility } from 'shared/lib';
import { GreenScreenVideo } from 'features/green-screen-video';
import { useTimerConversation } from 'entities/tavus/conversation/lib';
import { RiFullscreenExitLine } from 'react-icons/ri';
import {
  hotkeyDescriptions,
  useKeyboardFullScreenAndCaptionsBinding
} from 'widgets/call-controls/lib';
import { IoResizeOutline } from 'react-icons/io5';

export const TavusConference = () => {
  const { toggleFullScreen, videoContainer, isFullScreen } = useFullScreen();
  const { currentConversation, endConversation, setStartStreamTime } =
    conversationStore((state) => ({
      currentConversation: state.currentConversation,
      endConversation: state.endConversation,
      setStartStreamTime: state.setStartStreamTime
    }));

  const {
    startHairCheck,
    callObject,
    handleNewMeetingState,
    currentState,
    startLeavingCall,
    joinCall,
    addMessage,
    messages,
    withCaptions,
    toggleCaptions
  } = dailyCallStore();

  const hasJoinedRef = useRef(false);
  const hasSetStartTimeRef = useRef(false);

  useKeyboardFullScreenAndCaptionsBinding({
    toggleFullScreen,
    toggleCaptions
  });

  useEffect(() => {
    if (currentConversation) {
      startHairCheck(currentConversation.conversation_url);
    }
  }, [currentConversation]);

  useEffect(() => {
    if (!callObject) return;

    handleNewMeetingState();
    events.forEach((event) => callObject.on(event, handleNewMeetingState));

    callObject.on('transcription-message', (message) => addMessage(message));

    return () => {
      events.forEach((event) => callObject.off(event, handleNewMeetingState));
      transcriptionEvents.forEach((event) => callObject.off(event, () => {}));
    };
  }, [callObject]);

  const nav = useNavigate();

  const handleEndCall = () => {
    endConversation(messages);
    startLeavingCall();
    nav(MAIN_ROUTE);
  };

  useEffect(() => {
    return () => {
      handleEndCall();
    };
  }, []);

  useEffect(() => {
    if (
      currentState === DAILY_CALL_STATES.STATE_HAIRCHECK &&
      currentConversation &&
      !hasJoinedRef.current
    ) {
      hasJoinedRef.current = true;
      joinCall(currentConversation.conversation_url);
    }

    if (currentState === DAILY_CALL_STATES.STATE_ERROR) {
      handleEndCall();
    }
  }, [currentState]);

  useEffect(() => {
    if (callObject && currentConversation && !hasSetStartTimeRef.current) {
      callObject.on('participant-joined', (event) => {
        if (!event.participant.local) {
          hasSetStartTimeRef.current = true;
          setStartStreamTime(currentConversation.id, new Date().toISOString());
        }
      });
    }
  }, [callObject, currentConversation]);

  useTimerConversation(currentConversation, callObject);

  const isVisible = useMouseVisibility();
  if (!callObject) return null;

  return (
    <div
      ref={videoContainer}
      className="ag-bg-neutral-900 ag-rounded-xl ag-h-full ag-w-full ag-flex ag-items-center ag-justify-center ag-relative"
    >
      <DailyProvider callObject={callObject}>
        {[
          DAILY_CALL_STATES.STATE_JOINING,
          DAILY_CALL_STATES.STATE_JOINED,
          DAILY_CALL_STATES.STATE_ERROR
        ].includes(currentState) ? (
          <>
            <Video />
            <TavusControlBar onCallEnd={handleEndCall} />

            <div
              className={cn(
                'ag-flex ag-items-center ag-gap-6 ag-absolute ag-top-3.5 ag-right-3.5',
                isVisible ? 'ag-block' : 'ag-hidden'
              )}
            >
              <div className="ag-relative group">
                <ToolTip
                  className="ag-right-5 ag-top-5 ag-text-white"
                  text={hotkeyDescriptions.toggleCaptions}
                />
              </div>

              <button
                type="button"
                className="ag-absolute ag-top-3 ag-right-3 ag-rounded-[10px] ag-bg-white-opacity-70 ag-p-2 ag-h-10 ag-w-10 ag-flex ag-flex-col ag-justify-center ag-text-dark-purple"
                onClick={toggleFullScreen}
              >
                {isFullScreen ? (
                  <RiFullscreenExitLine size={24} />
                ) : (
                  <IoResizeOutline size={24} color="currentColor" />
                )}
                <ToolTip
                  className="ag-right-5 ag-top-5"
                  text={hotkeyDescriptions.toggleFullScreen}
                />
              </button>
            </div>
          </>
        ) : (
          <Loader size={96} />
        )}

        <DailyAudio />
      </DailyProvider>
    </div>
  );
};

const Video = () => {
  const remoteParticipantIds = useParticipantIds({ filter: 'remote' });
  const ref = useRef<HTMLVideoElement>(null);
  const currentPersona = tavusPersonaStore((state) => state.currentPersona);

  return (
    <div className="ag-relative ag-w-full ag-h-full ag-flex ag-justify-center">
      {remoteParticipantIds.length > 0 ? (
        remoteParticipantIds.slice(0, 1).map((id) => (
          <React.Fragment key={id}>
            <DailyVideo
              className="ag-absolute ag-inset-0 ag-object-cover ag-w-full ag-h-full"
              ref={ref}
              automirror
              sessionId={id}
              type="video"
              playableStyle={{ width: '100%', height: '100%' }}
              fit="cover"
            />
            <GreenScreenVideo
              background={currentPersona?.custom_background}
              videoRef={ref}
              classNames="ag-relative ag-opacity-0 ag-w-full ag-h-full ag-inset-0"
            />
          </React.Fragment>
        ))
      ) : (
        <div className="ag-w-full ag-h-full ag-flex ag-justify-center ag-items-center ag-pb-5 ag-flex-col">
          <Loader size={96} />
        </div>
      )}
    </div>
  );
};
